import { Component, ElementRef, OnInit, ViewChild, OnDestroy, Output, EventEmitter } from '@angular/core';
import { IntegrationDetails } from 'src/app/models/integration-details';
import { IntegrationAction } from 'src/app/models/integration-action';
import { BehaviorSubject, Subscription } from 'rxjs';
import { ActionService } from 'src/app/services/action.service';
import { IntegrationActionParameter } from 'src/app/models/integration-action-parameter';
import { DisplayService } from 'src/app/services/display.service';
import { StringLocalizerService } from 'src/app/services/string-localizer.service';
import { AdminService } from 'src/app/services/admin.service';
import { ConfigService } from 'src/app/services/config.service';
import { BusinessLogicService } from 'src/app/services/business-logic.service';
import { MatCheckboxChange } from '@angular/material/checkbox';


@Component({
    selector: 'app-action',
    templateUrl: './action.component.html',
    styleUrls: ['./action.component.css'],
    standalone: false
})
export class ActionComponent implements OnInit, OnDestroy {
  @ViewChild("chevron", { read: ElementRef }) chevron: ElementRef;
  @Output() change: EventEmitter<MatCheckboxChange>;
  public $selectedIntegration = new BehaviorSubject<IntegrationDetails | null>(null);
  public $integrationActions = new BehaviorSubject<IntegrationAction[]>([]);
  public integrationSelected: string = null;
  public processRunning: boolean = false;
  public processFinished: boolean = false;
  public actionDisabled: boolean = false;
  public isDrawerOpen: boolean = false;
  public runOnLocalHost: boolean = false;
  public localHostAccess: boolean = false;
  public strings: {} = {};
  public notProd: boolean = false;

  private subscriptions: Subscription = new Subscription();

  getActionDisplayName(action: IntegrationAction & { alias?: string }): string {
    return action?.alias ?? action?.name;
  }

  getIntegrationDisplayName(integrationName: string): string {
    const selectedIntegration = this.businessLogicService.listIntegrations().find(i => i.name === integrationName);
    return selectedIntegration?.alias ?? integrationName;
  }

  get integrationActions() {
    return this.$integrationActions.asObservable();
  }

  constructor(
    private actionService: ActionService,
    private displayService: DisplayService,
    private stringLocalizerService: StringLocalizerService,
    private adminService: AdminService,
    private configService: ConfigService,
    private businessLogicService: BusinessLogicService
  ) { 
    this.subscriptions.add(this.stringLocalizerService.strings.subscribe(s => {
      if(s){
        this.strings = this.stringLocalizerService.getSection("actions");
      }
    }));
    this.subscriptions.add(this.adminService.$myAccess.subscribe(myAccess => {
      if(myAccess != null && myAccess.disLocalhostPermission != null){
        this.localHostAccess = myAccess.disLocalhostPermission;
      }
    }));
    if (this.configService.getEnvironment() != "PROD") {
      this.notProd = true;
    }
  }

  ngOnInit(): void {
    this.subscriptions.add(this.displayService.$selectedActionSolution.subscribe(solution => {
      if (solution != null) {
        this.displayService.setSelectedSpanText(this.getIntegrationDisplayName(solution));
        this.actionService.selectIntegration(solution);
        this.integrationSelected = solution;
        this.loadIntegrationActions(solution);
        this.businessLogicService.toggleLocalHost(this.runOnLocalHost);
      }
    }));
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  toggleLocalHost(ob: MatCheckboxChange): void {
    this.runOnLocalHost = ob.checked; 
    this.businessLogicService.toggleLocalHost(this.runOnLocalHost); 
  }
  
  private loadIntegrationActions(integrationName: string) {
    const selectedIntegration = this.businessLogicService.listIntegrations().find(i => i.name === integrationName);
    if (selectedIntegration) {
      selectedIntegration.actions.forEach(a => {
        a.parameters.forEach(p => {
          if (p.dataType == 4) {
            const trueValues = ["true", "t", "TRUE", "True", "T"];
            if (typeof p.defaultValue === 'string') {
              p.defaultValue = trueValues.includes(p.defaultValue) ? true : false;
            }
          }
        })
      });
      this.$integrationActions.next(selectedIntegration.actions);
    }
  }

  public async runAction(actionName: string, params: IntegrationActionParameter[]) {
    this.businessLogicService.runIntegration(this.integrationSelected, actionName, params);
    this.actionDisabled = true;
    await this.delay(5000);
    this.actionDisabled = false;
  }
  
  public async runIntegrationAction(actionName: string, params: IntegrationActionParameter[]) {
    await this.runAction(actionName, params);
  }
  
  public async runDebugAction(actionName: string, params: IntegrationActionParameter[]) {
    await this.runAction(actionName, params);
  }


  async toggleSpinner() {
    this.processFinished = false;
    this.processRunning = true;
    await this.delay(5000);
    this.processRunning = false;
    this.processFinished = true;
  }

  delay(delay: number) {
    return new Promise(r => setTimeout(r, delay));
  }

  onlyNumberKey(event) {
    return (event.charCode == 8 || event.charCode == 0) ? null : event.charCode >= 48 && event.charCode <= 57;
  }

  onOpenedChange() {
    this.isDrawerOpen = !this.isDrawerOpen;
    if (!this.isDrawerOpen) {
      this.businessLogicService.loadIntegrationHistory(this.integrationSelected);
    }
  }
}